<template>
  <b-container class="products-page py-4">
    <h1 class="mb-4">Productos</h1>
    
    <b-row>
      <!-- Sidebar Filters -->
      <b-col lg="3" class="mb-4">
        <b-card title="Filtros" class="filters-card">
          <!-- Search -->
          <b-form-group label="Buscar" class="mb-3">
            <b-form-input 
              v-model="filters.search" 
              placeholder="Buscar productos..."
              @input="debounceSearch">
            </b-form-input>
          </b-form-group>

          <!-- Categories -->
          <b-form-group label="Categorías" class="mb-3">
            <b-form-select v-model="filters.category" @change="applyFilters">
              <b-form-select-option :value="null">Todas las categorías</b-form-select-option>
              <b-form-select-option 
                v-for="category in categories" 
                :key="category.id"
                :value="category.id">
                {{ category.name }}
              </b-form-select-option>
            </b-form-select>
          </b-form-group>

          <!-- Brands -->
          <b-form-group label="Marcas" class="mb-3">
            <b-form-checkbox-group 
              v-model="filters.brands"
              @change="applyFilters"
              stacked>
              <b-form-checkbox 
                v-for="brand in brands" 
                :key="brand.id"
                :value="brand.id">
                {{ brand.name }}
              </b-form-checkbox>
            </b-form-checkbox-group>
          </b-form-group>

          <!-- Price Range -->
          <b-form-group label="Rango de Precio" class="mb-3">
            <b-row>
              <b-col>
                <b-form-input 
                  v-model.number="filters.minPrice" 
                  type="number"
                  placeholder="Min"
                  @change="applyFilters">
                </b-form-input>
              </b-col>
              <b-col>
                <b-form-input 
                  v-model.number="filters.maxPrice" 
                  type="number"
                  placeholder="Max"
                  @change="applyFilters">
                </b-form-input>
              </b-col>
            </b-row>
          </b-form-group>

          <!-- Clear Filters -->
          <b-button variant="outline-secondary" block @click="clearFilters">
            Limpiar Filtros
          </b-button>
        </b-card>
      </b-col>

      <!-- Products Grid -->
      <b-col lg="9">
        <!-- Sort and View Options -->
        <b-row class="mb-3">
          <b-col md="6">
            <p class="text-muted">
              Mostrando {{ products.length }} productos
            </p>
          </b-col>
          <b-col md="6">
            <b-form-select v-model="sortBy" @change="sortProducts" size="sm">
              <b-form-select-option value="name">Nombre A-Z</b-form-select-option>
              <b-form-select-option value="name-desc">Nombre Z-A</b-form-select-option>
              <b-form-select-option value="price">Precio: Menor a Mayor</b-form-select-option>
              <b-form-select-option value="price-desc">Precio: Mayor a Menor</b-form-select-option>
            </b-form-select>
          </b-col>
        </b-row>

        <!-- Loading State -->
        <div v-if="loading" class="text-center py-5">
          <b-spinner large variant="primary"></b-spinner>
          <p class="mt-3">Cargando productos...</p>
        </div>

        <!-- Products Grid -->
        <b-row v-else-if="products.length > 0">
          <b-col 
            v-for="product in paginatedProducts" 
            :key="product.id"
            cols="12" 
            sm="6" 
            md="4"
            class="mb-4">
            <product-card :product="product" />
          </b-col>
        </b-row>

        <!-- No Products -->
        <b-alert v-else variant="info" show class="text-center">
          <b-icon icon="info-circle" font-scale="2"></b-icon>
          <p class="mt-3">No se encontraron productos con los filtros seleccionados.</p>
        </b-alert>

        <!-- Pagination -->
        <b-pagination
          v-if="totalPages > 1"
          v-model="currentPage"
          :total-rows="products.length"
          :per-page="perPage"
          align="center"
          class="mt-4">
        </b-pagination>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import ProductCard from '@/components/ProductCard.vue'

export default {
  name: 'ProductList',
  components: {
    ProductCard
  },
  data() {
    return {
      filters: {
        search: '',
        category: null,
        brands: [],
        minPrice: null,
        maxPrice: null
      },
      sortBy: 'name',
      currentPage: 1,
      perPage: 12,
      searchTimeout: null
    }
  },
  computed: {
    loading() {
      return this.$store.state.loading
    },
    products() {
      let filtered = [...this.$store.state.products]
      
      // Apply filters
      if (this.filters.search) {
        const search = this.filters.search.toLowerCase()
        filtered = filtered.filter(p => 
          p.name.toLowerCase().includes(search) ||
          (p.description && p.description.toLowerCase().includes(search))
        )
      }
      
      if (this.filters.category) {
        filtered = filtered.filter(p => p.category_id === this.filters.category)
      }
      
      if (this.filters.brands.length > 0) {
        filtered = filtered.filter(p => this.filters.brands.includes(p.brand_id))
      }
      
      if (this.filters.minPrice) {
        filtered = filtered.filter(p => p.price >= this.filters.minPrice)
      }
      
      if (this.filters.maxPrice) {
        filtered = filtered.filter(p => p.price <= this.filters.maxPrice)
      }
      
      // Apply sorting
      filtered.sort((a, b) => {
        switch(this.sortBy) {
          case 'name':
            return a.name.localeCompare(b.name)
          case 'name-desc':
            return b.name.localeCompare(a.name)
          case 'price':
            return a.price - b.price
          case 'price-desc':
            return b.price - a.price
          default:
            return 0
        }
      })
      
      return filtered
    },
    paginatedProducts() {
      const start = (this.currentPage - 1) * this.perPage
      const end = start + this.perPage
      return this.products.slice(start, end)
    },
    totalPages() {
      return Math.ceil(this.products.length / this.perPage)
    },
    categories() {
      return this.$store.state.categories
    },
    brands() {
      return this.$store.state.brands
    }
  },
  created() {
    this.loadInitialData()
    this.checkQueryParams()
  },
  methods: {
    async loadInitialData() {
      await Promise.all([
        this.$store.dispatch('loadProducts'),
        this.$store.dispatch('loadCategories'),
        this.$store.dispatch('loadBrands')
      ])
    },
    checkQueryParams() {
      if (this.$route.query.search) {
        this.filters.search = this.$route.query.search
      }
      if (this.$route.query.category) {
        this.filters.category = parseInt(this.$route.query.category)
      }
    },
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.applyFilters()
      }, 500)
    },
    applyFilters() {
      this.currentPage = 1
      // Aquí podrías hacer una nueva petición al servidor con los filtros
      // Por ahora solo filtramos en el frontend
    },
    clearFilters() {
      this.filters = {
        search: '',
        category: null,
        brands: [],
        minPrice: null,
        maxPrice: null
      }
      this.applyFilters()
    },
    sortProducts() {
      // El sorting se aplica en el computed
    }
  }
}
</script>

<style scoped>
.products-page {
  min-height: 60vh;
}

.filters-card {
  position: sticky;
  top: 80px;
}
</style>